import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"
import { type FormEvent, useState } from "react"

import { api, ErrorApi } from "@/api/cliente"
import { Boton } from "@/componentes/ui/Boton"
import { Modal } from "@/componentes/ui/Modal"

import type { Organigrama } from "./tipos"

type Area = { id: number; nombre: string; codigo: string | null; activo: boolean }

/**
 * Crear un organigrama o corregir uno: nombre, si es el general o el de un área, y
 * archivarlo.
 *
 * ⚠ **Un mapa de área nombra su área, y el general no tiene ninguna.** Lo exige el
 * servidor; aquí el selector de área sólo aparece con «De un área», y al volver a
 * «General» se manda el área vacía —si no, el servidor rechazaría el cambio—.
 *
 * ⚠ **Se monta de nuevo en cada apertura** (quien lo usa le cambia la `key`), por lo
 * mismo que el diálogo de departamento: un refresco no pisa lo tecleado.
 */
export function DialogoMapa({
  abierto,
  mapa,
  onCerrar,
  onGuardado,
}: {
  abierto: boolean
  /** `null` para crear uno nuevo. */
  mapa: Organigrama | null
  onCerrar: () => void
  onGuardado: (mapa: Organigrama) => void
}) {
  const clienteQuery = useQueryClient()
  const [nombre, setNombre] = useState(mapa?.nombre ?? "")
  const [tipo, setTipo] = useState<Organigrama["tipo"]>(mapa?.tipo ?? "GENERAL")
  const [area, setArea] = useState(
    mapa?.departamento_id == null ? "" : String(mapa.departamento_id),
  )

  const areas = useQuery({
    queryKey: ["departamentos"],
    queryFn: () => api.get<Area[]>("/api/v1/departamentos"),
    enabled: abierto && tipo === "DEPARTAMENTAL",
  })

  const guardar = useMutation({
    mutationFn: (cambios: Partial<Organigrama>) =>
      mapa === null
        ? api.post<Organigrama>("/api/v1/organigramas", cambios)
        : api.patch<Organigrama>(`/api/v1/organigramas/${mapa.id}`, cambios),
    onSuccess: (guardado) => {
      clienteQuery.invalidateQueries({ queryKey: ["organigramas"] })
      onGuardado(guardado)
    },
    meta: { exito: "Organigrama guardado" },
  })

  function enviar(evento: FormEvent) {
    evento.preventDefault()
    guardar.mutate({
      nombre: nombre.trim(),
      tipo,
      departamento_id: tipo === "DEPARTAMENTAL" && area !== "" ? Number(area) : null,
    })
  }

  const elegibles = (areas.data ?? []).filter((a) => a.activo || String(a.id) === area)

  return (
    <Modal
      abierto={abierto}
      titulo={mapa === null ? "Nuevo organigrama" : `Editar «${mapa.nombre}»`}
      descripcion="Puede haber varios: el general y los de cada área."
      onCerrar={onCerrar}
      ancho="sm"
    >
      <form
        onSubmit={enviar}
        aria-label={mapa === null ? "Nuevo organigrama" : "Editar organigrama"}
      >
        <label htmlFor="nombre-mapa" className="mb-1 block text-xs font-medium uppercase">
          Nombre
        </label>
        <input
          id="nombre-mapa"
          required
          minLength={2}
          maxLength={160}
          className="campo"
          value={nombre}
          onChange={(e) => setNombre(e.target.value)}
        />
        <fieldset className="mt-3">
          <legend className="mb-1 block text-xs font-medium uppercase">De qué es</legend>
          <label className="flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="tipo-mapa"
              checked={tipo === "GENERAL"}
              onChange={() => setTipo("GENERAL")}
            />
            De toda la agencia
          </label>
          <label className="mt-1 flex items-center gap-2 text-sm">
            <input
              type="radio"
              name="tipo-mapa"
              checked={tipo === "DEPARTAMENTAL"}
              onChange={() => setTipo("DEPARTAMENTAL")}
            />
            De un área
          </label>
        </fieldset>
        {tipo === "DEPARTAMENTAL" && (
          <>
            <label htmlFor="area-mapa" className="mt-3 mb-1 block text-xs font-medium uppercase">
              Área
            </label>
            <select
              id="area-mapa"
              required
              className="campo"
              value={area}
              onChange={(e) => setArea(e.target.value)}
            >
              <option value="">Elegir…</option>
              {elegibles.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.codigo ? `${a.codigo} · ` : ""}
                  {a.nombre}
                </option>
              ))}
            </select>
            {areas.isError && (
              <p className="mt-1 text-xs" style={{ color: "var(--fg-muted)" }}>
                No se pudieron leer las áreas: hace falta poder ver los departamentos.
              </p>
            )}
          </>
        )}
        {guardar.error && (
          <p role="alert" className="mt-3 text-sm" style={{ color: "var(--destructive)" }}>
            {guardar.error instanceof ErrorApi ? guardar.error.message : "No se pudo guardar"}
          </p>
        )}
        <div className="mt-4 flex flex-wrap justify-end gap-2">
          {mapa !== null && (
            <Boton
              variante="fantasma"
              className="mr-auto"
              disabled={guardar.isPending}
              onClick={() => guardar.mutate({ activo: !mapa.activo })}
            >
              {mapa.activo ? "Archivar organigrama" : "Volver a activar"}
            </Boton>
          )}
          <Boton variante="fantasma" onClick={onCerrar}>
            Cancelar
          </Boton>
          <Boton variante="primario" type="submit" disabled={guardar.isPending}>
            {mapa === null ? "Crear" : "Guardar"}
          </Boton>
        </div>
      </form>
    </Modal>
  )
}
